import { TypedDocumentNode, gql } from "apollo-angular";

export const SUBSCRIPTION_LIST_BASE_FRAGMENT = gql`
  fragment SubscriptionListBaseFragment on SubscriptionDetails {
    subscriptionId
    healthSubscriptionId
    description
    tags
    status
    customerDescription
    customer {
      customerId
      fullname
    }
    product {
      type
      tag
    }
  }
`;

// L2VPN and L3VPN list types do not implement the SubscriptionDetails interface,
// so they need their own fragments with the same base fields.
const L2VPN_LIST_BASE_FRAGMENT = gql`
  fragment L2VpnListBaseFragment on L2VpnSubscriptionType {
    subscriptionId
    healthSubscriptionId
    description
    tags
    status
    customerDescription
    customer {
      customerId
      fullname
    }
    product {
      type
      tag
    }
  }
`;

const L3VPN_LIST_BASE_FRAGMENT = gql`
  fragment L3VpnListBaseFragment on L3VpnSubscriptionType {
    subscriptionId
    healthSubscriptionId
    description
    tags
    status
    customerDescription
    customer {
      customerId
      fullname
    }
    product {
      type
      tag
    }
  }
`;

// ServicePortSubscriptionType also does not implement SubscriptionDetails.
const PORT_LIST_BASE_FRAGMENT = gql`
  fragment PortListBaseFragment on ServicePortSubscriptionType {
    subscriptionId
    healthSubscriptionId
    description
    tags
    status
    customerDescription
    customer {
      customerId
      fullname
    }
    product {
      type
      tag
    }
  }
`;

const SERVICE_TYPE_FRAGMENT = gql`
  fragment ServiceTypeFragment on ServiceType {
    subscriptionId
    portSubscriptionInstanceId
    product {
      type
      tag
    }
    speed
    percentage
    description
  }
`;

const PORT_SERVICE_TYPE_FRAGMENT = gql`
  fragment PortServiceTypeFragment on PortServiceType {
    subscriptionId
    productType
    speed
    percentage
    description
  }
`;

export const PORT_SUBSCRIPTIONS_OF_TYPE_QUERY_2 = gql`
  ${PORT_LIST_BASE_FRAGMENT}
  ${SERVICE_TYPE_FRAGMENT}
  query PortAccessCustomerListQuery($customer: UUID!) {
    subscriptions: portList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        ...PortListBaseFragment
        portSpeed
        portMode
        speed
        amountReserved
        services {
          ...ServiceTypeFragment
        }
        locations {
          address {
            city
            number
            street
            addition
          }
        }
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const PORT_SUBSCRIPTIONS_OF_CUSTOMER_QUERY = gql`
  ${PORT_LIST_BASE_FRAGMENT}
  ${SERVICE_TYPE_FRAGMENT}
  query PortCustomerListQuery($customer: UUID!) {
    subscriptions: portList(sort: { subscriptionId: ASC }, searchFilter: { customerId: $customer }) {
      page {
        ...PortListBaseFragment
        portSpeed
        portMode
        speed
        amountReserved
        usedVlans
        services {
          ...ServiceTypeFragment
        }
        address {
          city
          street
        }
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const L3VPN_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${L3VPN_LIST_BASE_FRAGMENT}
  query L3vpnListQuery($customer: UUID!) {
    subscriptions: l3vpnList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        __typename
        ...L3VpnListBaseFragment
        locations {
          address {
            city
            number
            street
            addition
          }
        }
        firewallEnabled
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const L2VPN_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${L2VPN_LIST_BASE_FRAGMENT}
  query L2vpnListQuery($customer: UUID!) {
    subscriptions: l2vpnList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        __typename
        ...L2VpnListBaseFragment
        locations {
          address {
            city
            number
            street
            addition
          }
        }
        firewallEnabled
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const LP_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${SUBSCRIPTION_LIST_BASE_FRAGMENT}
  ${PORT_SERVICE_TYPE_FRAGMENT}
  query LightPathListQuery($customer: UUID!) {
    subscriptions: evpnPtpList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        __typename
        ...SubscriptionListBaseFragment
        locations {
          address {
            city
            number
            street
            addition
          }
        }
        services {
          ...PortServiceTypeFragment
        }
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const IP_PREFIXES_LIST_QUERY: TypedDocumentNode<any, any> = gql`
  query IpPrefixesListQuery($customer: UUID!) {
    ipPrefixList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer, status: ACTIVE }) {
      page {
        subscriptionType: __typename
        subscriptionId
        customerId
        description
        startDate
        customerDescription
        insync

        ipPrefix {
          afi
          state
          prefix
        }

        ipSubscriptions {
          subscriptionId
          customerId
          description
          customerDescription
          product {
            type
            tag
          }
        }

        availableIpSubscriptions {
          subscriptionId
          customerId
          description
          customerDescription
          product {
            type
            tag
          }
        }
      }
    }
  }
`;

export const WIRELESS_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${SUBSCRIPTION_LIST_BASE_FRAGMENT}
  query WirelessSubscriptionListQuery($customer: UUID!) {
    subscriptions: wirelessList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        __typename
        ...SubscriptionListBaseFragment
        name
        supplier
        sensorPresent
        jiraLocationId
        location {
          city
        }
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const WIRED_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${SUBSCRIPTION_LIST_BASE_FRAGMENT}
  query WiredSubscriptionListQuery($customer: UUID!) {
    subscriptions: wiredList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        __typename
        ...SubscriptionListBaseFragment
        name
        monitoringEnabled
        customCustomerAbbreviation
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const IP_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${SUBSCRIPTION_LIST_BASE_FRAGMENT}
  query IpListQuery($customer: UUID!) {
    subscriptions: ipList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        ...SubscriptionListBaseFragment
        locations {
          address {
            city
            number
            street
            addition
          }
        }
        protection
      }
      pageInfo {
        totalItems
      }
    }
  }
`;

export const FIREWALL_SUBSCRIPTIONS_OF_TYPE_QUERY = gql`
  ${SUBSCRIPTION_LIST_BASE_FRAGMENT}
  query FirewallListQuery($customer: UUID!) {
    subscriptions: firewallList(sort: { subscriptionId: ASC }, searchFilter: { accessCustomerId: $customer }) {
      page {
        ...SubscriptionListBaseFragment
      }
      pageInfo {
        totalItems
      }
    }
  }
`;
